import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import urlJoin from 'url-join';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';

import { LoadingPage, ProcessingModal, useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import {
  ScreenHeader,
  Footer,
  FooterNextButton,
  FooterPreviousButton,
  Body,
} from 'QuoteApp/components';
import { Disclosures } from 'QuoteApp/services/endpoints/GetDisclosures';
import {
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import { getErrorMessage } from 'QuoteApp/utils';
import { useFooter } from 'QuoteApp/utils/hooks';
import { useAuth0 } from 'Auth';
import {
  ESignatureErrorsType,
  PurchaseProvider,
  useAppContext,
  usePurchaseContext,
} from 'QuoteApp/services/providers';
import {
  ContactInfo,
  ContactInfoV2,
  MainContent,
  ProcessOneModal,
  PurchaseFormValuesType,
  SidebarContent,
} from '.';
import { CheckoutLinkContent } from './CheckoutLinkContent';
import { PurchaseModeToggle } from './PurchaseModeToggle';
import { UWConsentModal } from './UWConsentModal';
import {
  AutopayDownpaymentModal,
  AutopayModalState,
} from './AutopayDownpaymentModal';
import { findCurrentPlan, parseDownpaymentAmount } from './utils';
import { AgentSignatureContent } from './AgentSignatureContent';
import { PurchaseAlertBanners } from './PurchaseAlertBanners';
import { PurchaseDisclaimer } from './PurchaseDisclaimer';
import { PricePlanConfigurator } from './PriceCustomizer';
import { SWEEP_DUMMY_TOKEN } from './constants';
import styles from './styles.module.css';

/**
 * Syncs the autopayEnabled value to app_data and refreshes payment plans.
 * On save failure, rolls back the optimistic update and shows an error toast.
 */
const SweepAutopaySync: FC<{
  autopayEnabled: boolean;
  isSweepPurchase: boolean;
  onAutopayChanged: (autopay: boolean) => void;
  onAutopayRollback: (autopay: boolean) => void;
  onAutopayToggledOff?: () => void;
  onDownpaymentResult?: (
    result: 'increased' | 'no-change',
    oldDownpayment: string,
    newDownpayment: string
  ) => void;
  onRateError?: () => void;
  currentDownpayment?: string;
  showError: (message: string) => void;
}> = ({
  autopayEnabled,
  isSweepPurchase,
  onAutopayChanged,
  onAutopayRollback,
  onAutopayToggledOff,
  onDownpaymentResult,
  onRateError,
  currentDownpayment,
  showError,
}) => {
  const { api, fetchRate } = useAppContext();
  const prevAutopayEnabled = useRef(autopayEnabled);

  useEffect(() => {
    if (!isSweepPurchase) return;
    if (prevAutopayEnabled.current === autopayEnabled) return;
    const previousValue = prevAutopayEnabled.current;
    prevAutopayEnabled.current = autopayEnabled;

    const wasToggledOff = previousValue && !autopayEnabled;
    const savedDownpayment = wasToggledOff ? currentDownpayment : undefined;

    if (wasToggledOff && onAutopayToggledOff) {
      onAutopayToggledOff();
    }

    // Update details optimistically so enableReinitialize preserves the
    // toggled value even if a re-render happens before saveAppData resolves.
    onAutopayChanged(autopayEnabled);

    api
      .saveAppData({
        data: { paymentPreferences: { autopay: autopayEnabled } },
      })
      .then(() => fetchRate())
      .then(newRate => {
        if (
          wasToggledOff &&
          savedDownpayment &&
          onDownpaymentResult &&
          newRate
        ) {
          const newPaymentDetails = newRate.paymentDetails?.attributes;
          const newPlan =
            newPaymentDetails && findCurrentPlan(newPaymentDetails);
          if (newPlan) {
            const oldAmount = parseDownpaymentAmount(savedDownpayment);
            const newAmount = parseDownpaymentAmount(newPlan.downPaymentAmount);
            if (
              !isNaN(oldAmount) &&
              !isNaN(newAmount) &&
              newAmount > oldAmount
            ) {
              onDownpaymentResult(
                'increased',
                savedDownpayment,
                newPlan.downPaymentAmount
              );
            } else {
              onDownpaymentResult(
                'no-change',
                savedDownpayment,
                newPlan.downPaymentAmount
              );
            }
          } else {
            onDownpaymentResult(
              'no-change',
              savedDownpayment,
              savedDownpayment
            );
          }
        }
      })
      .catch(() => {
        if (wasToggledOff && onRateError) {
          onRateError();
        }
        // Rollback: restore details and Formik field to previous value
        prevAutopayEnabled.current = previousValue;
        onAutopayChanged(previousValue);
        onAutopayRollback(previousValue);
        showError('Failed to update autopay preference. Please try again.');
      });
  }, [
    isSweepPurchase,
    autopayEnabled,
    api,
    fetchRate,
    onAutopayChanged,
    onAutopayRollback,
    onAutopayToggledOff,
    onDownpaymentResult,
    onRateError,
    currentDownpayment,
    showError,
  ]);

  return null;
};

export const Purchase: FC = () => {
  const initialRef = useRef(true);
  const { toast } = useToast();
  const { previousStep } = useFooter(5);

  const {
    appDataStale,
    isEditingContactInfo,
    setIsEditingContactInfo,
    paymentDetails,
    processing,
    eSignatureSMSConsent,
    eSignatureErrors,
    setESignatureErrors,
    scrollIntoESignatureView,
    agentConfirmation,
    signaturePacketSendAttempt,
    postConfirmation,
    submitBind,
  } = usePurchaseContext();

  const { user } = useAuth0();
  const {
    appId,
    details,
    config,
    configLoading,
    setDetails,
    refreshRate,
  } = useAppContext();
  const history = useHistory();

  const isSweepPurchase = !!config?.sweepEnabled;

  // Keep details.paymentPreferences.autopay in sync so enableReinitialize
  // doesn't reset autopayEnabled back to the stale server value.
  const handleAutopayChanged = useCallback(
    (autopay: boolean) => {
      setDetails(prev => ({
        ...prev,
        paymentPreferences: { ...prev.paymentPreferences, autopay },
      }));
    },
    [setDetails]
  );

  // TODO: Pull from the configuration response - https://clearcover.atlassian.net/browse/RVL-1492
  const isOchoAgent = user?.partner?.name === 'Ocho Insurance Solutions LLC';

  // This is part of a short-term fix to support Bind API and AS 2.0
  // For now Ocho is the only partner that uses AgentPay
  const isOchoPolicy = paymentDetails?.paymentProvider === 'agentPay';
  const disableAgentSignatures = !details?.commands?.sendSignaturePacket;
  const isPaymentFlexibilityEnabled = !!config?.flexiblePayments.enabled;
  const isCheckoutLinkEnabled = !!config?.checkoutLink?.enabled;

  const [isCheckoutLinkMode, setIsCheckoutLinkMode] = useState(
    isCheckoutLinkEnabled
  );
  const [showUWConsentModal, setShowUWConsentModal] = useState(false);
  const [showAutopayModal, setShowAutopayModal] = useState(false);
  const [autopayModalState, setAutopayModalState] = useState<AutopayModalState>(
    'loading'
  );
  const [autopayModalAmounts, setAutopayModalAmounts] = useState({
    previous: '',
    current: '',
  });

  useEffect(() => {
    setIsCheckoutLinkMode(isCheckoutLinkEnabled);
  }, [isCheckoutLinkEnabled]);

  const handleSwitchToDirectCheckout = useCallback(() => {
    const uwRequired = details.underwritingConsent?.required;
    const uwAlreadyGiven = !!details.underwritingConsent?.givenAt;

    if (uwRequired && !uwAlreadyGiven) {
      setShowUWConsentModal(true);
    } else {
      setIsCheckoutLinkMode(false);
    }
  }, [details.underwritingConsent]);

  const handleModeChange = useCallback(
    (isCheckoutLink: boolean) => {
      if (!isCheckoutLink) {
        handleSwitchToDirectCheckout();
      } else {
        setIsCheckoutLinkMode(true);
      }
    },
    [handleSwitchToDirectCheckout]
  );

  const isDocumentSigningHandledByPartner =
    !disableAgentSignatures && isOchoAgent;
  const policyDocumentsSigned =
    !!details.commands.sendSignaturePacket &&
    !details.commands.sendSignaturePacket.runnable;
  const hasPolicyDocumentsBeenSigned =
    isDocumentSigningHandledByPartner && policyDocumentsSigned;

  const purchaseDisclosureDocument = details.disclosures.find(
    doc => doc.attributes.documentName === Disclosures.Purchase
  );

  const purchaseDisclosureCompleted = !!purchaseDisclosureDocument?.attributes
    .completed;

  const [, postDisclosure] = useLazyQuoteApi<GetDetailsResponseType>({
    method: 'POST',
    url: `/insurance_applications/${appId}/document_signatures/${Disclosures.Purchase}/complete`,
    transformResponse: transformAppDetails,
  });

  const handleDisclosure = useCallback(async () => {
    // If there are payment errors this could trigger multiple times; only complete once
    if (!purchaseDisclosureCompleted) {
      try {
        const completedDisclosureData = await postDisclosure();

        // refresh updated details
        setDetails(completedDisclosureData.data);
      } catch (e) {
        toast({
          type: 'error',
          message: `Error capturing disclosure: ${getErrorMessage(
            e
          )}. Please try again.`,
          withSecondaryNav: false,
          icon: 'error',
        });

        // throw again to stop bind
        throw new Error('Failed to capture disclosure');
      }
    }
  }, [postDisclosure, purchaseDisclosureCompleted, setDetails, toast]);

  const handlePostAgentConfirmation = useCallback(async () => {
    if (agentConfirmation) {
      const completedDisclosureData = await postConfirmation();

      // refresh updated details
      setDetails(completedDisclosureData);
    }
  }, [postConfirmation, setDetails, agentConfirmation]);

  const handleSignaturePacketValidation = useCallback((): boolean => {
    let valid = true;
    const errors: ESignatureErrorsType = {};

    if (!disableAgentSignatures) {
      const signatureCompleted = isDocumentSigningHandledByPartner
        ? hasPolicyDocumentsBeenSigned
        : signaturePacketSendAttempt;

      if (
        !isDocumentSigningHandledByPartner &&
        eSignatureSMSConsent === undefined
      ) {
        errors.smsConsent =
          'Please select whether the customer would like to receive these documents via text message.';
        valid = false;
      }
      if (!signatureCompleted) {
        errors.sentSingnaturePacket =
          'Signature packet must be sent before completing purchase.';
        valid = false;
      }
    }

    if (!agentConfirmation) {
      errors.agentConfirmation = 'Must check all agreements.';
      valid = false;
    }

    if (Object.keys(errors).length > 0) {
      setESignatureErrors(errors);
    }

    return valid;
  }, [
    eSignatureSMSConsent,
    signaturePacketSendAttempt,
    agentConfirmation,
    isDocumentSigningHandledByPartner,
    hasPolicyDocumentsBeenSigned,
    disableAgentSignatures,
    setESignatureErrors,
  ]);

  useEffect(() => {
    // force to refresh rate only run one time on-loading
    if (initialRef.current) {
      refreshRate();
    }
    return () => {
      initialRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!paymentDetails || appDataStale || configLoading) {
    return (
      <LoadingPage
        message={
          appDataStale
            ? 'Please wait while we fetch the most recent application data...'
            : ''
        }
      />
    );
  }

  const defaultPaymentPlan =
    findCurrentPlan(
      paymentDetails,
      details.paymentPreferences.paymentPlanEnum
    ) ?? paymentDetails.paymentPlans[0];

  const disableNextButton =
    processing ||
    !details.commands.bind.runnable ||
    isEditingContactInfo ||
    isOchoPolicy;

  return (
    <Body data-testid="purchase-page" fullWidth>
      <Formik<PurchaseFormValuesType>
        enableReinitialize
        initialValues={{
          paymentMethod: defaultPaymentPlan.paymentMethodIdentifier,
          paymentPlanId: defaultPaymentPlan.identifier,
          quoteId: paymentDetails.quoteId,
          adyenForm: null,
          contactInfo: {
            [details.principalInsuredId]: {
              email:
                details.contactInfo[details.principalInsuredId].email.value,
              phoneNumber:
                details.contactInfo[details.principalInsuredId].phoneNumber
                  .value,
              phoneType:
                details.contactInfo[details.principalInsuredId].phoneType.value,
            },
          },
          sweepAffirmationAccepted: false,
          autopayEnabled: details.paymentPreferences.autopay ?? true,
        }}
        onSubmit={async values => {
          // Form onSubmit handles Adyen Payments
          // ProcessOne is handled via ProcessOneModal in footer

          // In checkout-link mode the consumer handles signing and payment entirely —
          // the agent cannot and should not trigger disclosure or Adyen submission.
          if (isCheckoutLinkMode) return;

          // Validate signature packet and post agent confirmation then post payment disclosure
          const attemptToSendSignaturePacket = handleSignaturePacketValidation();
          if (!attemptToSendSignaturePacket) {
            scrollIntoESignatureView();
            return;
          }
          await handlePostAgentConfirmation();

          // Sweep: validate affirmation BEFORE side-effecting API calls
          if (
            isSweepPurchase &&
            values.autopayEnabled &&
            !values.sweepAffirmationAccepted
          ) {
            setESignatureErrors({
              ...eSignatureErrors,
              sweepAffirmation: 'Must check the disclosure.',
            });
            // Trigger Adyen validation so CC field errors also appear
            if (
              paymentDetails.paymentProvider === 'adyen' ||
              paymentDetails.paymentProvider === 'agentPay'
            ) {
              values.adyenForm?.submit();
            }
            return;
          }

          await handleDisclosure();

          // Sweep with autopay OFF: skip Adyen, bind directly
          // (SweepAutopaySync already persisted autopay=false on toggle)
          if (isSweepPurchase && !values.autopayEnabled) {
            await submitBind(values, SWEEP_DUMMY_TOKEN);
            return;
          }

          // final step submit adyenForm payment
          if (
            paymentDetails.paymentProvider === 'adyen' ||
            paymentDetails.paymentProvider === 'agentPay'
          ) {
            values.adyenForm?.submit();
          }
        }}
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => {
          return (
            <>
              <SweepAutopaySync
                autopayEnabled={values.autopayEnabled}
                isSweepPurchase={isSweepPurchase}
                onAutopayChanged={handleAutopayChanged}
                onAutopayRollback={(prev: boolean) =>
                  setFieldValue('autopayEnabled', prev)
                }
                currentDownpayment={defaultPaymentPlan.downPaymentAmount}
                onAutopayToggledOff={() => {
                  setAutopayModalState('loading');
                  setShowAutopayModal(true);
                }}
                onDownpaymentResult={(result, oldAmount, newAmount) => {
                  setAutopayModalAmounts({
                    previous: oldAmount,
                    current: newAmount,
                  });
                  setAutopayModalState(result);
                }}
                onRateError={() => {
                  setShowAutopayModal(false);
                }}
                showError={(message: string) =>
                  toast({
                    type: 'error',
                    message,
                    withSecondaryNav: false,
                    icon: 'error',
                  })
                }
              />
              <div className={styles.container}>
                <ScreenHeader headerText="Review & complete purchase" />
                <PurchaseAlertBanners />
                {isPaymentFlexibilityEnabled && <PricePlanConfigurator />}
                {isCheckoutLinkEnabled && (
                  <PurchaseModeToggle
                    isCheckoutLink={isCheckoutLinkMode}
                    onChange={handleModeChange}
                  />
                )}
                {processing && <ProcessingModal />}
                <div
                  className={classNames(
                    styles.bodyWrapper,
                    isCheckoutLinkEnabled ? styles.checkoutLinkBodyWrapper : '',
                    !isCheckoutLinkMode && !disableAgentSignatures
                      ? styles.agentSignatureBodyWrapper
                      : ''
                  )}
                >
                  {isCheckoutLinkMode ? (
                    <div className={styles.checkoutLinkMainWrapper}>
                      <div className={styles.contactInfoSection}>
                        {!disableAgentSignatures ? (
                          <ContactInfoV2
                            setIsEditing={setIsEditingContactInfo}
                          />
                        ) : (
                          <ContactInfo
                            isEditing={isEditingContactInfo}
                            setIsEditing={setIsEditingContactInfo}
                          />
                        )}
                      </div>
                      <CheckoutLinkContent
                        onSwitchToDirectCheckout={handleSwitchToDirectCheckout}
                        onSent={() =>
                          history.push(
                            urlJoin(
                              QUOTE_APP_ROUTE,
                              appId,
                              'checkout-link-congrats'
                            )
                          )
                        }
                      />
                    </div>
                  ) : (
                    <MainContent paymentDetails={paymentDetails} />
                  )}
                  <SidebarContent paymentDetails={paymentDetails} />
                </div>
                {!isCheckoutLinkMode && (
                  <AgentSignatureContent
                    policyDocumentsSigned={hasPolicyDocumentsBeenSigned}
                    documentSigningHandledByAgent={
                      isDocumentSigningHandledByPartner
                    }
                    disableAgentSignatures={disableAgentSignatures}
                  />
                )}
              </div>
              <UWConsentModal
                open={showUWConsentModal}
                onCancel={() => setShowUWConsentModal(false)}
                onAccepted={() => {
                  setShowUWConsentModal(false);
                  setIsCheckoutLinkMode(false);
                }}
              />
              <AutopayDownpaymentModal
                open={showAutopayModal}
                state={autopayModalState}
                previousDownpayment={autopayModalAmounts.previous}
                newDownpayment={autopayModalAmounts.current}
                onEnableAutopay={() => {
                  setFieldValue('autopayEnabled', true);
                  setShowAutopayModal(false);
                }}
                onUnderstand={() => {
                  setShowAutopayModal(false);
                }}
                onContinue={() => {
                  setShowAutopayModal(false);
                }}
              />
              <Footer
                hideRate
                disclaimer={<PurchaseDisclaimer />}
                previousButton={
                  <FooterPreviousButton
                    onClick={previousStep.onClickSuccess}
                    disabled={!!processing}
                  >
                    {previousStep.text}
                  </FooterPreviousButton>
                }
                nextButton={
                  paymentDetails.paymentProvider === 'processOne' ? (
                    <ProcessOneModal
                      paymentDetails={paymentDetails}
                      handleDisclosure={handleDisclosure}
                      handleSignaturePacketValidation={
                        handleSignaturePacketValidation
                      }
                      handlePostAgentConfirmation={handlePostAgentConfirmation}
                      disabled={disableNextButton}
                    />
                  ) : (
                    <FooterNextButton
                      onClick={() => {
                        submitForm();
                      }}
                      isSubmitting={isSubmitting || processing}
                      disabled={isCheckoutLinkMode ? true : disableNextButton}
                    >
                      Complete Purchase
                    </FooterNextButton>
                  )
                }
              />
            </>
          );
        }}
      </Formik>
    </Body>
  );
};

export const PurchaseWrapper: FC = () => (
  <PurchaseProvider>{() => <Purchase />}</PurchaseProvider>
);
